import React from 'react';
import type { EvidenceTier } from '@/lib/types';
import { cn } from '@/lib/utils';

/**
 * TierChip — 4-tier evidence badge.
 *
 * Renders the v3.0 reserved-hue chips from `shared.css` `.tier` (ported to
 * `styles/globals.css`). These colors are reserved for evidence-tier signals
 * only — never repurpose `#16A34A` / `#3B82F6` / `#F59E0B` / `#9CA3AF` for
 * general UI states.
 *
 * `as="span"` by default for inline use; switch to `"div"` inside flex
 * containers if a block-level element is required.
 */

export type TierChipSize = 'sm' | 'md';

export interface TierChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  tier: EvidenceTier;
  /** Optional override label; defaults to the standard tier label. */
  label?: string;
  /** Size — `md` matches the spec default; `sm` is for dock/queue items. */
  size?: TierChipSize;
  /** Prefix with an extra context word, e.g. "Strong · Magnesium". */
  suffix?: string;
}

const TIER_LABEL: Record<EvidenceTier, string> = {
  strong: 'Strong',
  moderate: 'Moderate',
  preliminary: 'Preliminary',
  insufficient: 'Insufficient',
};

const TIER_CLASS: Record<EvidenceTier, string> = {
  strong: 'tier tier--strong',
  moderate: 'tier tier--moderate',
  preliminary: 'tier tier--preliminary',
  insufficient: 'tier tier--insufficient',
};

export function TierChip({
  tier,
  label,
  size = 'md',
  suffix,
  className,
  ...rest
}: TierChipProps) {
  const text = label ?? TIER_LABEL[tier];
  return (
    <span
      className={cn(
        TIER_CLASS[tier],
        size === 'sm' && '!text-[9px] !px-1.5 !py-[1px]',
        className
      )}
      {...rest}
    >
      {text}
      {suffix ? <span className="opacity-70"> · {suffix}</span> : null}
    </span>
  );
}

export default TierChip;
